import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { KTIcon } from '../../../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { getDownloadCouponById } from '../../../../../services/CouponService';
import { useSelector } from 'react-redux';

interface DownloadCouponModalProps {
  show: boolean;
  onHide: () => void;
  couponId: number | null;
}

const DownloadCouponModal: React.FC<DownloadCouponModalProps> = ({
  show,
  onHide,
  couponId,
}) => {
  // Get organization name from Redux state
  const user = useSelector((state: any) => state.auth.user);
  const organizationName = user?.organization_dtls?.name?.toLowerCase()?.trim() || '';
  
  // Fallback: Check URL hostname if organization name is not available
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const urlBasedOrg = hostname.includes('synergy') ? 'synergy' : 
                      hostname.includes('shrika') ? 'shrika' : 
                      hostname.includes('developerbox') ? 'developerbox' : '';

  // Determine which options to show based on organization (use URL as fallback)
  const orgName = organizationName || urlBasedOrg;
  const isSynergy = orgName === 'synergy' || orgName.includes('synergy');
  const isShrika = orgName === 'shrika' || orgName.includes('shrika');
  const isDeveloperBox = orgName === 'developerbox' || orgName.includes('developerbox');

  // Set default value based on organization
  const getDefaultGridSize = () => {
    if (isSynergy) return 'polygum-coupon';
    if (isShrika) return '13x5';
    return '13x5'; // fallback default
  };

  const [selectedGridSize, setSelectedGridSize] = useState<string>(getDefaultGridSize());
  const [downloading, setDownloading] = useState(false);

  // Reset grid size to default when modal opens
  useEffect(() => {
    if (show && couponId) {
      setSelectedGridSize(getDefaultGridSize());
    }
  }, [show, couponId, isSynergy, isShrika, isDeveloperBox]);

  /**
   * Handles the download of coupon QR code PDF with selected grid size
   */
  const handleDownload = async () => {
    if (!couponId) return;
    
    setDownloading(true);
    try {
      const response = await getDownloadCouponById(couponId, selectedGridSize);

      // Check if response contains PDF URL
      const pdfUrl = response.data?.data?.coupon_qr_codes_url || response.data?.coupon_qr_codes_url;

      if (pdfUrl) {
        // Open PDF in new tab
        window.open(pdfUrl, '_blank');
        toast.success('Coupon PDF downloaded successfully');
        onHide(); // Close modal after successful download
      } else {
        toast.error('PDF URL not found in response');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.reason || error?.message || 'Failed to download coupon PDF';
      toast.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // Get available options based on organization
  const getAvailableOptions = () => {
    // Priority 1: Shrika - Show only 13x5 and 7x3
    // Check both organization name and URL to ensure Shrika is detected
    if (isShrika || hostname.includes('shrika')) {
      return [
        { value: '13x5', label: '13x5' },
        { value: '7x3', label: '7x3' },
      ];
    }
    
    // Priority 2: Synergy - Show only polygum-coupon and ombond-coupon
    if (isSynergy || hostname.includes('synergy')) {
      return [
        { value: 'polygum-coupon', label: 'polygum-coupon' },
        { value: 'ombond-coupon', label: 'ombond-coupon' },
      ];
    }
    
    // Priority 3: DeveloperBox - Show all options
    if (isDeveloperBox || hostname.includes('developerbox')) {
      return [
        { value: '13x5', label: '13x5' },
        { value: '7x3', label: '7x3' },
        { value: 'polygum-coupon', label: 'polygum-coupon' },
        { value: 'ombond-coupon', label: 'ombond-coupon' },
      ];
    }
    
    // Fallback: Default to Shrika options (13x5 and 7x3) for unrecognized organizations
    // This ensures Shrika always gets correct options even if detection fails
    return [
      { value: '13x5', label: '13x5' },
      { value: '7x3', label: '7x3' },
    ];
  };

  const availableOptions = getAvailableOptions();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Download Coupon QR Code</h2>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
            onClick={onHide}
          >
            <KTIcon iconName="cross" className="svg-icon svg-icon-2x" />
          </button>
        </div>

        <div className="modal-body">
          <div className="mb-4">
            <label htmlFor="gridSize" className="form-label fw-bold">
              Select Grid Size <span className="text-danger">*</span>
            </label>
            <select
              id="gridSize"
              className="form-select form-select-lg"
              value={selectedGridSize}
              onChange={(e) => setSelectedGridSize(e.target.value)}
              disabled={downloading}
            >
              {availableOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="form-text">
              Choose the grid size for the QR code layout
            </div>
            <div className="mt-2">
              <small className="text-info">
                Currently selected: <strong>{selectedGridSize}</strong>
              </small>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={onHide} disabled={downloading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={downloading || !selectedGridSize}
          >
            {downloading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Downloading...
              </>
            ) : (
              <>
                <KTIcon iconName="download" className="svg-icon-2 me-2" />
                Download PDF
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DownloadCouponModal;